import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, resolveAsset } from "../lib/api";
import { Trophy, Medal, Award } from "lucide-react";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

function initials(n) {
  if (!n) return "?";
  return n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

export default function LeaderboardPage() {
  const [tournaments, setTournaments] = useState([]);
  const [scope, setScope] = useState("all"); // all | tournamentId
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    (async () => {
      const { data: ts } = await api.get("/tournaments");
      setTournaments(ts);
      const rows = [];
      await Promise.all(
        ts.map(async (t) => {
          const { data } = await api.get(`/tournaments/${t.id}`);
          data.teams.forEach((team) => rows.push({ ...team, tournament_name: t.name }));
        })
      );
      setTeams(rows);
    })();
  }, []);

  const sorted = useMemo(() => {
    const filtered = scope === "all" ? teams : teams.filter((t) => t.tournament_id === scope);
    // aggregate by team name for "all"
    if (scope === "all") {
      const map = new Map();
      filtered.forEach((t) => {
        const key = t.name.toLowerCase();
        const e = map.get(key);
        if (e) { e.wins += t.wins || 0; e.losses += t.losses || 0; }
        else map.set(key, { name: t.name, color: t.color, logo_url: t.logo_url, wins: t.wins || 0, losses: t.losses || 0 });
      });
      return Array.from(map.values()).sort((a, b) => b.wins - a.wins || a.losses - b.losses);
    }
    return [...filtered].sort((a, b) => (b.wins || 0) - (a.wins || 0) || (a.losses || 0) - (b.losses || 0));
  }, [scope, teams]);

  const medal = (i) =>
    i === 0 ? <Trophy size={16} className="text-yellow-400" /> :
    i === 1 ? <Medal size={16} className="text-zinc-300" /> :
    i === 2 ? <Award size={16} className="text-amber-600" /> : null;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">Global rankings</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">Leaderboard</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Top performing squads by wins, losses, and total points. Points = Wins × 3.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          <button
            onClick={() => setScope("all")}
            data-testid="lb-scope-all"
            className={`px-5 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-all ${
              scope === "all" ? "bg-red-600 text-white shadow-[0_0_18px_rgba(230,0,0,0.5)]" : "glass-dark text-zinc-400 hover:text-white"
            }`}
          >
            Overall
          </button>
          {tournaments.map((t) => (
            <button
              key={t.id}
              onClick={() => setScope(t.id)}
              data-testid={`lb-scope-${t.id}`}
              className={`px-5 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-all ${
                scope === t.id ? "bg-red-600 text-white shadow-[0_0_18px_rgba(230,0,0,0.5)]" : "glass-dark text-zinc-400 hover:text-white"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>

        <div className="glass-dark rounded-2xl overflow-hidden">
          <div className="grid grid-cols-[44px_1fr_56px_56px_72px] sm:grid-cols-[60px_1fr_80px_80px_80px] gap-2 sm:gap-3 px-4 sm:px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <div className="text-center">Rank</div>
            <div>Team</div>
            <div className="text-center">W</div>
            <div className="text-center">L</div>
            <div className="text-center">Points</div>
          </div>
          {sorted.map((t, i) => (
            <div
              key={t.name + i}
              data-testid={`lb-row-${i}`}
              className="grid grid-cols-[44px_1fr_56px_56px_72px] sm:grid-cols-[60px_1fr_80px_80px_80px] gap-2 sm:gap-3 px-4 sm:px-5 py-4 items-center border-b border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center justify-center text-sm font-mono text-zinc-400">
                {medal(i) || <span>#{i + 1}</span>}
              </div>
              <div className="flex items-center gap-3 min-w-0">
                {t.logo_url ? (
                  <img src={resolveAsset(t.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="h-8 w-8 rounded-full flex items-center justify-center font-display font-bold text-xs" style={{ background: t.color || "#E60000" }}>
                    {initials(t.name)}
                  </div>
                )}
                <span className="font-medium truncate">{t.name}</span>
              </div>
              <div className="text-center font-mono font-bold text-emerald-400">{t.wins}</div>
              <div className="text-center font-mono font-bold text-red-400">{t.losses}</div>
              <div className="text-center font-mono font-bold text-red-300">{t.wins * 3}</div>
            </div>
          ))}
          {sorted.length === 0 && (
            <div className="py-12 text-center text-zinc-500">No teams yet.</div>
          )}
        </div>
      </div>
      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}
